"use client";

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Image from 'next/image';
import { Search as SearchIcon, X, ArrowRight } from 'lucide-react';
import Fuse from 'fuse.js';
import { useRouter } from 'next/navigation';
import { getSearchIndex, SearchItem } from '@/utils/searchIndex';
import { cn } from '@/lib/utils';

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * FIX SC 2.1.2 [A] + SC 4.1.2 [A]: Focus trap utility for modal dialogs.
 */
const FOCUSABLE_SELECTORS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]';

const SearchOverlay = ({ isOpen, onClose }: SearchOverlayProps) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const searchIndex = getSearchIndex();
  const fuse = new Fuse(searchIndex, {
    keys: ['title', 'description', 'tags'],
    threshold: 0.3,
  });

  /**
   * FIX SC 2.4.3 [A]: Move focus INTO modal on open.
   */
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults([]);
      // Use requestAnimationFrame for more reliable focus in some browsers
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isOpen]);

  /**
   * FIX SC 2.1.2 [A]: Trap focus inside the search modal.
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      /**
       * FIX SC 1.4.13 [AA]: Escape closes the dialog.
       */
      onClose();
      return;
    }
    
    if (e.key !== 'Tab') return;

    const focusable = Array.from(
      overlayRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS) ?? []
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [onClose]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (val.length > 1) {
      const searchResults = fuse.search(val).map(r => r.item);
      setResults(searchResults);
    } else {
      setResults([]);
    }
  };

  const handleSelect = (url: string) => {
    router.push(url);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      /**
       * <!-- FIX SC 1.3.1 [A] + SC 4.1.2 [A]: Proper modal role and status. -->
       */
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label="Search site content"
      onKeyDown={handleKeyDown}
      className="fixed inset-0 z-[100] bg-bloom-charcoal/80 backdrop-blur-[4px] animate-in fade-in duration-200 p-4 md:p-8 flex flex-col items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-[600px] mt-12 md:mt-20 flex flex-col">
        {/* Search Box */}
        <div className="bg-bloom-cream rounded-xl shadow-2xl overflow-hidden border border-bloom-border flex flex-col max-h-[70vh]">
          <div className="relative flex items-center border-b border-bloom-border px-4 py-4">
            <SearchIcon className="text-bloom-green-mid" size={20} aria-hidden="true" />
            <input
              ref={inputRef}
              type="search"
              placeholder="Search site content..."
              className="flex-1 bg-transparent border-none focus:ring-0 px-3 py-1 text-base font-sans text-bloom-charcoal placeholder:text-bloom-muted/70 outline-none"
              value={query}
              onChange={handleSearch}
              /**
               * <!-- FIX SC 3.3.2 [A]: Input has an accessible name via aria-label. -->
               */
              aria-label="Search terms"
              /**
               * <!-- FIX SC 4.1.3 [AA]: Search results quantity is semi-announced. -->
               */
              aria-controls="search-results-list"
            />
            <button 
              onClick={onClose}
              className="p-2 text-bloom-muted hover:text-bloom-charcoal transition-colors focus-bloom rounded ml-2"
              aria-label="Close search"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>

          <div 
            id="search-results-list"
            className="overflow-y-auto bg-card"
            /**
             * <!-- FIX SC 4.1.3 [AA]: Use a live region to announce when results update. -->
             */
            role="region"
            aria-live="polite"
            aria-atomic="false"
          >
            {results.length > 0 ? (
              <div className="py-2">
                <p className="sr-only">{results.length} results found.</p>
                {results.map((item, idx) => (
                  <button
                    key={`${item.url}-${idx}`}
                    onClick={() => handleSelect(item.url)}
                    className="w-full text-left px-4 py-4 hover:bg-bloom-linen transition-colors group flex items-start gap-4 focus-bloom border-b border-bloom-border/50 last:border-0"
                  >
                    {/* Thumbnail: OG image if available, else letter avatar */}
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 mt-0.5 border border-bloom-border/40" aria-hidden="true">
                      {item.image ? (
                        <div className="relative w-full h-full">
                          <Image
                            src={item.image}
                            alt=""
                            fill
                            className="object-cover object-top"
                            sizes="48px"
                          />
                        </div>
                      ) : (
                        <div className="w-full h-full bg-bloom-green-light/10 flex items-center justify-center">
                          <span className="text-[0.65rem] font-bold text-bloom-green-mid uppercase tracking-widest">
                            {item.type.charAt(0)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col mb-1">
                        <span className="text-[0.65rem] font-sans font-bold text-bloom-muted uppercase tracking-[0.1em] leading-none mb-1">
                          {item.type}
                        </span>
                        <h3 className="text-base font-serif font-bold text-bloom-charcoal truncate">
                          {item.title}
                        </h3>
                      </div>
                      <p className="text-sm text-bloom-muted truncate leading-normal">
                        {item.description}
                      </p>
                    </div>
                    <ArrowRight size={16} className="text-bloom-green-mid/40 group-hover:text-bloom-green-mid transition-colors mt-3" aria-hidden="true" />
                  </button>
                ))}
              </div>
            ) : query.length > 1 ? (
              <div className="p-12 text-center">
                <p className="text-base font-sans text-bloom-muted">No results found for "{query}"</p>
                <p className="text-sm text-bloom-muted/70 mt-2">Try searching for keywords like "accessibility" or "nonprofit".</p>
              </div>
            ) : (
              <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {['Nonprofit', 'Accessibility', 'Government', 'Grant Readiness'].map(term => (
                  <button 
                    key={term}
                    onClick={() => { setQuery(term); setResults(fuse.search(term).map(r => r.item)); }}
                    className="p-4 rounded-xl border border-bloom-border bg-bloom-cream hover:bg-bloom-linen text-left transition-colors flex flex-col focus-bloom"
                    aria-label={`Search for ${term}`}
                  >
                    <p className="text-[0.6rem] font-sans font-bold uppercase tracking-widest text-bloom-muted/80 mb-1" aria-hidden="true">Common Topic</p>
                    <p className="text-sm font-serif font-bold text-bloom-charcoal">{term}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="px-5 py-3 border-t border-bloom-border bg-bloom-linen/30 flex justify-between items-center">
            <div className="flex gap-4 text-[0.7rem] font-sans text-bloom-muted font-medium uppercase tracking-widest">
              <span>ESC to close</span>
              <span>ENTER to select</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchOverlay;
